import cn from 'classnames';
import { MdCancel, MdCheckCircle, MdMoreHoriz } from 'react-icons/md';
import { Dropdown } from '../../../../../../components/dropdown/dropdown';
import { InfiniteScroll } from '../../../../../../components/infinite-scroll/infinite-scroll';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { useEmployeesStore } from '../../../../../../store/use-employee-store';
import { useEmployeesUtils } from '../use-employees-utils';
import s from './employees-cards.module.css';

interface Props {
  onEnd: () => void;
}

export const EmployeesCards = ({ onEnd }: Props) => {
  const { staff, isLoadingFetch } = useEmployeesStore();
  const {
    t,
    getLocale,
    createEmployeeModal,
    deleteEmployeeModal,
    qrEmployeeModal,
    unlinkEmployeeModal,
  } = useEmployeesUtils();

  if (!isLoadingFetch && !staff?.length) {
    return <div>{t('Здесь пока нет сотрудников')}</div>;
  }

  return (
    <ul className={s.feature}>
      {staff?.map((item) => {
        const isOnShift = item.shift_state === 'ON_SHIFT';

        return (
          <li key={item.id} className={s.item} id={`card-${item.id}`}>
            <div className={s.top}>
              <div className={s.status}>
                <div className={cn(s.onlineStatus, isOnShift ? s.online : s.offline)}>
                  {isOnShift ? <MdCheckCircle /> : <MdCancel />}
                </div>

                <div>
                  <div className={s.title}>{t('Статус')}</div>
                  <div className={s.subtitle}>{t(isOnShift ? 'На смене' : 'Не на смене')}</div>
                </div>
              </div>

              <HasAccess service="staff" permissions={['write']}>
                <Dropdown
                  optionsClassName={s.dropdown}
                  withChevron={false}
                  leftIcon={<MdMoreHoriz className={s.actions} />}
                  closeOnSelect
                  options={[
                    item.online
                      ? {
                          label: t('Разавторизовать'),
                          key: 'unauthorize',
                          onClick: () =>
                            unlinkEmployeeModal.handleOpenModal({
                              id: item.id,
                              name: `${getLocale(item.name)} ${getLocale(item.surname)}`,
                            }),
                        }
                      : {
                          label: t('Авторизовать'),
                          key: 'authorize',
                          onClick: () =>
                            qrEmployeeModal.handleOpenModal({
                              id: item.id,
                              staffName: `${getLocale(item.name)} ${getLocale(item.surname)}`,
                              type: 'login',
                            }),
                        },
                    {
                      label: t('Зарегистрировать'),
                      key: 'register',
                      onClick: () =>
                        qrEmployeeModal.handleOpenModal({
                          id: item.id,
                          staffName: `${getLocale(item.name)} ${getLocale(item.surname)}`,
                          type: 'register',
                        }),
                    },
                    {
                      label: t('Редактировать'),
                      key: 'edit',
                      onClick: () => createEmployeeModal.handleOpenModal({ id: item.id }),
                    },
                    {
                      label: t('Удалить'),
                      key: 'delete',
                      onClick: () => deleteEmployeeModal.handleOpenModal({ id: item.id }),
                    },
                  ]}
                />
              </HasAccess>
            </div>

            <div className={s.main}>
              <div>
                <span className={s.title} style={{ marginRight: 17 }}>
                  {t('Филиал')}
                </span>
                <span className={s.subtitle}>
                  {item.places?.map(({ value }) => getLocale(value)).join(', ')}
                </span>
              </div>

              <div className={s.employee}>
                {getLocale(item.surname)} {getLocale(item.name)}
              </div>
            </div>

            <div>
              <div className={s.title}>{t('Должности')}</div>
              <div className={s.subtitle}>{item.tags.map((tag) => tag.value).join(', ')}</div>
            </div>
          </li>
        );
      })}

      <InfiniteScroll onEnterView={onEnd} />
    </ul>
  );
};
